import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonList, IonItem, IonLabel, IonButton, IonIcon, IonInput, IonSelect, IonSelectOption,
  IonChip, IonSpinner, IonModal
} from '@ionic/react';
import {
  searchOutline, filterOutline, personOutline, checkmarkOutline,
  closeOutline, createOutline, trashOutline, refreshOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { api, apiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { toast } from '@/store/ui.store';

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  role: string;
  status: string;
  createdAt: string;
}

interface UsersResponse {
  data: AdminUser[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface UpdateUserPatch {
  role?: 'owner' | 'admin';
  status?: 'active' | 'disabled';
  plan?: 'free' | 'starter' | 'pro' | 'business';
}

const ROLES: Array<{ value: 'owner' | 'admin'; label: string }> = [
  { value: 'owner', label: 'admin.owner' },
  { value: 'admin', label: 'admin.admin' },
];

const STATUSES: Array<{ value: 'active' | 'disabled'; label: string }> = [
  { value: 'active', label: 'admin.active' },
  { value: 'disabled', label: 'admin.disabled' },
];

const PLANS: Array<{ value: 'free' | 'starter' | 'pro' | 'business'; label: string }> = [
  { value: 'free', label: 'admin.planFree' },
  { value: 'starter', label: 'admin.planStarter' },
  { value: 'pro', label: 'admin.planPro' },
  { value: 'business', label: 'admin.planBusiness' },
];

export default function UsersPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPlan, setFilterPlan] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<string>('');
  const [editStatus, setEditStatus] = useState<string>('');
  const [editPlan, setEditPlan] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<UsersResponse>('/admin/users', {
        params: {
          search: search || undefined,
          role: filterRole || undefined,
          status: filterStatus || undefined,
          plan: filterPlan || undefined,
          page,
          limit,
        },
      });
      setUsers(data.data);
      setTotal(data.total);
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, [search, filterRole, filterStatus, filterPlan, page]);

  const handleSave = async () => {
    if (!editingUserId) return;
    setSaving(true);
    try {
      const patch: UpdateUserPatch = {};
      if (editRole) patch.role = editRole as 'owner' | 'admin';
      if (editStatus) patch.status = editStatus as 'active' | 'disabled';
      if (editPlan) patch.plan = editPlan as 'free' | 'starter' | 'pro' | 'business';
      await api.patch(`/admin/users/${editingUserId}`, patch);
      toast(t('admin.userUpdated'), 'success');
      closeEdit();
      await loadUsers();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (targetId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    setSaving(true);
    try {
      await api.patch(`/admin/users/${targetId}`, { status: newStatus as 'active' | 'disabled' });
      toast(newStatus === 'disabled' ? t('admin.userDeactivated') : t('admin.userActivated'), 'success');
      await loadUsers();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u: AdminUser) => {
    setEditingUserId(u.id);
    setEditRole(u.role);
    setEditStatus(u.status);
    setEditPlan(u.plan);
  };

  const closeEdit = () => {
    setEditingUserId(null);
    setEditRole('');
    setEditStatus('');
    setEditPlan('');
  };

  const isSelf = (id: string) => user?.id === id;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('admin.userList')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void loadUsers()}><IonIcon slot="icon-only" icon={refreshOutline} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : (
          <div className="page-shell page-shell--flush">
            <h6 className="section-title">{t('admin.userListSubtitle')}</h6>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <IonItem lines="none" detail={false} button={false} style={{ flex: 1, minWidth: 240 }}>
                <IonInput
                  placeholder={t('admin.searchPlaceholder')}
                  value={search}
                  onIonInput={(e) => setSearch(String((e.target as HTMLIonInputElement).value ?? ''))}
                  clearInput
                  slot="start"
                />
              </IonItem>
              <IonSelect placeholder={t('admin.role')} value={filterRole}
                onIonChange={(e) => setFilterRole(e.detail.value as string)} interface="popover">
                {ROLES.map((r) => (<IonSelectOption key={r.value} value={r.value}>{t(r.label)}</IonSelectOption>))}
              </IonSelect>
              <IonSelect placeholder={t('admin.status')} value={filterStatus}
                onIonChange={(e) => setFilterStatus(e.detail.value as string)} interface="popover">
                {STATUSES.map((s) => (<IonSelectOption key={s.value} value={s.value}>{t(s.label)}</IonSelectOption>))}
              </IonSelect>
              <IonSelect placeholder={t('admin.plan')} value={filterPlan}
                onIonChange={(e) => setFilterPlan(e.detail.value as string)} interface="popover">
                {PLANS.map((p) => (<IonSelectOption key={p.value} value={p.value}>{t(p.label)}</IonSelectOption>))}
              </IonSelect>
            </div>

            <IonList inset>
              {users.length === 0 ? (
                <IonItem lines="none" detail={false} button={false}>
                  <IonLabel>{t(total === 0 ? 'admin.noUsers' : 'admin.noUsersFound')}</IonLabel>
                </IonItem>
              ) : (
                users.map((u) => (
                  <IonItem key={u.id} lines="full">
                    <IonLabel>
                      <h3>{u.name ?? u.email}</h3>
                      <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>{u.email}</p>
                    </IonLabel>
                    <IonChip color={u.role === 'admin' ? 'warning' : 'medium'}>{t(u.role === 'admin' ? 'admin.admin' : 'admin.owner')}</IonChip>
                    <IonChip color={u.status === 'active' ? 'success' : 'danger'}>{t(u.status === 'active' ? 'admin.active' : 'admin.disabled')}</IonChip>
                    <IonChip color="medium">{u.plan.charAt(0).toUpperCase() + u.plan.slice(1)}</IonChip>
                    <IonButtons slot="end">
                      {!isSelf(u.id) && (
                        <IonButton size="small" fill="outline" onClick={() => void handleToggleStatus(u.id, u.status)}>
                          <IonIcon slot="icon-only" icon={u.status === 'active' ? trashOutline : checkmarkOutline} />
                        </IonButton>
                      )}
                      <IonButton size="small" fill="outline" onClick={() => void openEdit(u)}>
                        <IonIcon slot="icon-only" icon={createOutline} />
                      </IonButton>
                    </IonButtons>
                  </IonItem>
                ))
              )}
            </IonList>

            {total > limit && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginTop: 16 }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--ion-color-medium)' }}>
                  Page {page} of {Math.ceil(total / limit)}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <IonButton size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>
                    {t('common.previous')}
                  </IonButton>
                  <IonButton size="small" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>
                    {t('common.next')}
                  </IonButton>
                </div>
              </div>
            )}
          </div>
        )}

        <IonModal isOpen={editingUserId !== null} onDidDismiss={closeEdit}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={closeEdit}>
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('admin.editUser')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div style={{ padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <IonItem>
                <IonIcon slot="start" icon={personOutline} />
                <IonLabel position="stacked">{t('admin.changeRole')}</IonLabel>
                <IonSelect
                  value={editRole}
                  interface="popover"
                  onIonChange={(e) => setEditRole(e.detail.value as string)}
                >
                  {ROLES.map((r) => (
                    <IonSelectOption key={r.value} value={r.value}>{t(r.label)}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonIcon slot="start" icon={filterOutline} />
                <IonLabel position="stacked">{t('admin.changeStatus')}</IonLabel>
                <IonSelect
                  value={editStatus}
                  interface="popover"
                  onIonChange={(e) => setEditStatus(e.detail.value as string)}
                >
                  {STATUSES.map((s) => (
                    <IonSelectOption key={s.value} value={s.value}>{t(s.label)}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonIcon slot="start" icon={searchOutline} />
                <IonLabel position="stacked">{t('admin.changePlan')}</IonLabel>
                <IonSelect
                  value={editPlan}
                  interface="popover"
                  onIonChange={(e) => setEditPlan(e.detail.value as string)}
                >
                  {PLANS.map((p) => (
                    <IonSelectOption key={p.value} value={p.value}>{t(p.label)}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonButton expand="block" disabled={saving} onClick={() => void handleSave()}>
                {saving ? <IonSpinner name="dots" /> : t('admin.saveChanges')}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}